import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { useTraining } from '../lib/training-context';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  GraduationCap, CheckCircle2, Circle, Rocket, Users, MessageSquare, Package,
  Wallet, LifeBuoy, Award, Lock, ArrowRight, RotateCcw, Sparkles, Trophy,
  BookOpenCheck, MousePointerClick, HelpCircle, CalendarDays, UserCheck, Share2,
  FileDown, MessageSquareText,
} from 'lucide-react';
import { OpenAnswersDialog } from '../components/training/OpenAnswersDialog';
import { TeamAnswersDialog } from '../components/training/TeamAnswersDialog';

// ★ Fase 32 — locale per formattazione date coerente con la lingua UI
const dateLocale = () => {
  const lng = i18n.language || 'it';
  if (lng.startsWith('es')) return 'es-ES';
  if (lng.startsWith('en')) return 'en-GB';
  return 'it-IT';
};

// ★ 2026-08-30 (Fase 58.P) — download attestato PDF (blob autenticato)
export async function downloadCertificate(url = '/training/certificate', filename) {
  try {
    const res = await api.get(url, { responseType: 'blob' });
    const dispo = res.headers?.['content-disposition'] || '';
    const match = dispo.match(/filename="?([^";]+)"?/);
    const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = match ? match[1] : (filename || i18n.t('trainingPage.certFilename', 'Attestato_HCF.pdf'));
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
    toast.success(i18n.t('trainingPage.certDownloaded', 'Attestato scaricato'));
  } catch (err) {
    toast.error(err?.response?.status === 403
      ? i18n.t('trainingPage.certOnlyAfter', "L'attestato è disponibile solo dopo aver completato il training")
      : i18n.t('trainingPage.certDownloadError', 'Errore download attestato'));
  }
}

// ★ 2026-08-29 (Fase 58.M) — Training Gamificato Agenti.
// ★ 2026-08-30 (Fase 58.N) — v2 multi-percorso: il backend invia SOLO i moduli
// del percorso dell'utente (base / associato / senior / affiliato).

const MODULE_ICONS = {
  Rocket, Users, MessageSquare, Package, Wallet, LifeBuoy, Award, GraduationCap,
  CalendarDays, UserCheck, Share2, Trophy,
};

// ★ Fase 32 — etichette tipo missione localizzate a render-time
const TYPE_META = {
  visit: { icon: MousePointerClick, labelKey: 'trainingPage.typeVisit', labelDefault: 'Esplora' },
  action: { icon: Sparkles, labelKey: 'trainingPage.typeAction', labelDefault: 'Azione reale' },
  quiz: { icon: HelpCircle, labelKey: 'trainingPage.typeQuiz', labelDefault: 'Quiz' },
  open: { icon: MessageSquareText, labelKey: 'trainingPage.typeOpen', labelDefault: 'Risposta libera' },
};

function MissionRow({ mission, onGo, onQuiz, onOpen }) {
  const { t } = useTranslation();
  const meta = TYPE_META[mission.type] || TYPE_META.visit;
  const TypeIcon = meta.icon;
  return (
    <div
      className={`flex items-start gap-3 rounded-[var(--radius-sm)] border p-3 transition-colors ${
        mission.done
          ? 'border-[#BFE3D3] bg-[#EAF6F0]'
          : 'border-border bg-card hover:bg-[hsl(var(--accent))]'
      }`}
      data-testid={`training-mission-${mission.key}`}
    >
      {mission.done ? (
        <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0 text-[#1F7A5A]" data-testid={`training-mission-${mission.key}-done`} />
      ) : (
        <Circle className="w-5 h-5 mt-0.5 shrink-0 text-muted-foreground/50" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-semibold ${mission.done ? 'text-[#0C3C2D]' : ''}`}>{mission.title}</span>
          <Badge variant="outline" className="text-[10px] gap-1 px-1.5 py-0">
            <TypeIcon className="w-3 h-3" />{t(meta.labelKey, meta.labelDefault)}
          </Badge>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-[#F3E7C7] border-[#E4D3A0] text-[#6A4B00]">
            +{mission.points} pt
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{mission.desc}</p>
      </div>
      {!mission.done && (
        mission.type === 'quiz' ? (
          <Button size="sm" variant="secondary" className="shrink-0" onClick={() => onQuiz(mission)} data-testid={`training-quiz-btn-${mission.key}`}>
            <BookOpenCheck className="w-4 h-4 mr-1" /> {t('trainingPage.typeQuiz', 'Quiz')}
          </Button>
        ) : mission.type === 'open' ? (
          <Button size="sm" variant="secondary" className="shrink-0" onClick={() => onOpen(mission)} data-testid={`training-open-btn-${mission.key}`}>
            <MessageSquareText className="w-4 h-4 mr-1" /> {t('trainingPage.answerBtn', 'Rispondi')}
          </Button>
        ) : (
          <Button size="sm" variant="secondary" className="shrink-0" onClick={() => onGo(mission)} data-testid={`training-go-btn-${mission.key}`}>
            {t('trainingPage.goBtn', 'Vai')} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        )
      )}
    </div>
  );
}

export function QuizDialog({ mission, open, onClose, onPassed }) {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (open) setAnswers({}); }, [open, mission?.key]);

  if (!mission) return null;
  const questions = mission.questions || [];
  const allAnswered = questions.every((_, i) => answers[i] !== undefined);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post('/training/quiz', {
        mission_key: mission.key,
        answers: questions.map((_, i) => answers[i]),
      });
      if (data.ok) {
        toast.success(t('trainingPage.quizPassed', { defaultValue: 'Quiz superato! +{{points}} punti', points: data.points }), {
          description: data.finished
            ? t('trainingPage.trainingDone', 'Training completato: Certificato HCF ottenuto!')
            : t('trainingPage.correctAnswers', { defaultValue: '{{correct}}/{{total}} risposte corrette', correct: data.correct, total: data.total }),
        });
        onPassed();
        onClose();
      } else {
        toast.error(t('trainingPage.wrongAnswers', { defaultValue: '{{wrong}} risposta/e errate su {{total}}', wrong: data.wrong_count, total: data.total }), {
          description: t('trainingPage.retryQuiz', 'Riprova: rileggi le domande con calma.'),
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('trainingPage.quizSendError', 'Errore invio quiz'));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="training-quiz-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpenCheck className="w-5 h-5 text-[hsl(var(--primary))]" /> {mission.title}
          </DialogTitle>
          <DialogDescription>{mission.desc}</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {questions.map((q, qi) => (
            <div key={q.q} className="space-y-2">
              <p className="text-sm font-semibold">{qi + 1}. {q.q}</p>
              <RadioGroup
                value={answers[qi] !== undefined ? String(answers[qi]) : ''}
                onValueChange={(v) => setAnswers((a) => ({ ...a, [qi]: Number(v) }))}
              >
                {q.options.map((opt, oi) => (
                  <div key={opt} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 hover:bg-[hsl(var(--accent))]">
                    <RadioGroupItem value={String(oi)} id={`q${qi}-o${oi}`} data-testid={`training-quiz-${mission.key}-q${qi}-opt${oi}`} />
                    <Label htmlFor={`q${qi}-o${oi}`} className="text-sm font-normal cursor-pointer flex-1">{opt}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="training-quiz-cancel">{t('trainingPage.close', 'Chiudi')}</Button>
          <Button onClick={submit} disabled={!allAnswered || submitting} data-testid="training-quiz-submit">
            {submitting ? t('trainingPage.verifying', 'Verifica…') : t('trainingPage.submitAnswers', 'Invia risposte')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BadgesStrip({ badges }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-2" data-testid="training-badges-strip">
      {badges.map((b) => {
        const Icon = MODULE_ICONS[b.icon] || Award;
        return (
          <div
            key={b.key}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border ${
              b.earned
                ? 'bg-[#F3E7C7] border-[#D6B25E] text-[#6A4B00] shadow-[var(--shadow-sm)]'
                : 'bg-[var(--surface-3)] border-border text-muted-foreground opacity-60'
            }`}
            data-testid={`training-badge-${b.key}`}
            title={b.earned ? t('trainingPage.badgeEarned', 'Badge ottenuto') : t('trainingPage.badgeLocked', 'Badge da sbloccare')}
          >
            <Icon className="w-3.5 h-3.5" /> {b.title}
          </div>
        );
      })}
    </div>
  );
}

function TeamProgress() {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [answersRow, setAnswersRow] = useState(null); // ★ Fase 59 — dialog risposte libere
  useEffect(() => {
    api.get('/training/admin/progress')
      .then((r) => setRows(r.data.rows || []))
      .catch(() => setRows([]));
  }, []);

  if (rows === null) return <Skeleton className="h-48" />;
  if (!rows.length) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-8 text-center text-muted-foreground" data-testid="training-team-empty">
          {t('trainingPage.noAgents', 'Nessun agente da monitorare.')}
        </CardContent>
      </Card>
    );
  }
  const statusBadge = (s) => {
    if (s === 'completato') return <Badge className="bg-[#D7EFE6] text-[#0C3C2D] hover:bg-[#D7EFE6]">{t('trainingPage.statusCompleted', 'Completato')}</Badge>;
    if (s === 'in_corso') return <Badge className="bg-[#F3E7C7] text-[#6A4B00] hover:bg-[#F3E7C7]">{t('trainingPage.statusInProgress', 'In corso')}</Badge>;
    return <Badge variant="secondary">{t('trainingPage.statusNotStarted', 'Non iniziato')}</Badge>;
  };
  // ★ Fase 58.N — etichette percorso compatte per la tabella team
  const trackBadge = (r) => {
    const map = {
      affiliate: { label: t('trainingPage.trackAffiliate', 'Affiliato'), cls: 'bg-[#EDE4F7] text-[#4C2A85]' },
      associate: { label: t('trainingPage.trackAssociate', 'Associato'), cls: 'bg-[#D6F0F0] text-[#0E5A5A]' },
      senior: { label: t('trainingPage.trackSenior', 'Senior'), cls: 'bg-[#F3E7C7] text-[#6A4B00]' },
      staff: { label: t('trainingPage.trackStaff', 'Staff'), cls: 'bg-[var(--surface-3)] text-muted-foreground' },
    };
    const m = map[r.track_role] || map.staff;
    return <Badge className={`${m.cls} hover:${m.cls}`} data-testid={`training-team-track-${r.user_id}`}>{m.label}</Badge>;
  };
  return (
    <Card>
      <CardContent className="p-0 overflow-x-auto">
        <Table data-testid="training-team-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t('trainingPage.thAgent', 'Agente / Affiliato')}</TableHead>
              <TableHead>{t('trainingPage.thTrack', 'Percorso')}</TableHead>
              <TableHead>{t('trainingPage.thStatus', 'Stato')}</TableHead>
              <TableHead className="min-w-[160px]">{t('trainingPage.thProgress', 'Progresso')}</TableHead>
              <TableHead className="text-right">{t('trainingPage.thMissions', 'Missioni')}</TableHead>
              <TableHead className="text-right">{t('trainingPage.thPoints', 'Punti')}</TableHead>
              <TableHead className="text-right">{t('trainingPage.thBadges', 'Badge')}</TableHead>
              <TableHead>{t('trainingPage.thCompletedAt', 'Completato il')}</TableHead>
              <TableHead className="w-[52px]">{t('trainingPage.thAnswers', 'Risposte')}</TableHead>
              <TableHead className="w-[52px]">PDF</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.user_id} data-testid={`training-team-row-${r.user_id}`}>
                <TableCell>
                  <div className="font-medium text-sm">{r.name}</div>
                  <div className="text-xs text-muted-foreground">{r.email}</div>
                </TableCell>
                <TableCell>{trackBadge(r)}</TableCell>
                <TableCell>{statusBadge(r.status)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Progress value={r.percent} className="h-2 w-28" />
                    <span className="text-xs font-semibold tabular-nums">{r.percent}%</span>
                  </div>
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums">{r.done}/{r.total}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{r.points}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{r.badges}/{r.badges_total}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {r.first_completed_at ? new Date(r.first_completed_at).toLocaleDateString(dateLocale()) : '—'}
                </TableCell>
                <TableCell>
                  {r.has_answers && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2"
                      title={t('trainingPage.readAnswers', 'Leggi le risposte libere')}
                      onClick={() => setAnswersRow(r)}
                      data-testid={`training-team-answers-${r.user_id}`}
                    >
                      <MessageSquareText className="w-4 h-4" />
                    </Button>
                  )}
                </TableCell>
                <TableCell>
                  {r.first_completed_at && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2"
                      title={t('trainingPage.downloadCert', 'Scarica attestato')}
                      onClick={() => downloadCertificate(`/training/admin/certificate/${r.user_id}`)}
                      data-testid={`training-team-certificate-${r.user_id}`}
                    >
                      <FileDown className="w-4 h-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
      {/* ★ Fase 59 — lettura risposte libere */}
      <TeamAnswersDialog
        row={answersRow}
        open={!!answersRow}
        onClose={() => setAnswersRow(null)}
      />
    </Card>
  );
}

export default function TrainingPage() {
  const { t } = useTranslation();
  const { user } = useAuth() || {};
  const { state, refresh } = useTraining();
  const nav = useNavigate();
  const [quizMission, setQuizMission] = useState(null);
  const [openMission, setOpenMission] = useState(null); // ★ Fase 59 — domande libere
  const [restartOpen, setRestartOpen] = useState(false);

  const isAdmin = ['admin', 'amministrativo'].includes(user?.role);
  const loading = !state;
  const completed = !!state?.completed_at;
  const locked = !!state?.locked;
  const progress = state?.progress || { done: 0, total: 0, percent: 0, points: 0, points_total: 0 };

  const nextMission = useMemo(() => {
    if (!state) return null;
    for (const mod of state.modules || []) {
      const m = (mod.missions || []).find((x) => !x.done);
      if (m) return m;
    }
    return null;
  }, [state]);

  const restart = async () => {
    try {
      await api.post('/training/restart');
      toast.success(t('trainingPage.restartSuccess', 'Training riattivato: buon ripasso!'));
      setRestartOpen(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('trainingPage.restartError', 'Errore riavvio training'));
    }
  };

  const myTraining = (
    <div className="space-y-4">
      {/* Header hero */}
      <Card className="overflow-hidden">
        <CardContent className="p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
            <div className="w-14 h-14 rounded-2xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] flex items-center justify-center shrink-0">
              <GraduationCap className="w-7 h-7" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-serif text-xl md:text-2xl text-[hsl(var(--primary))]" data-testid="training-title">
                {t('trainingPage.pageTitle', 'Training HCF Property')}
              </h1>
              {/* ★ Fase 58.N — badge percorso assegnato in base al ruolo */}
              {state?.track_label && (
                <Badge variant="outline" className="mt-1 bg-[var(--surface-3)] text-[hsl(var(--primary))] border-[hsl(var(--primary))]/25" data-testid="training-track-badge">
                  <GraduationCap className="w-3 h-3 mr-1" /> {state.track_label}
                </Badge>
              )}
              <p className="text-sm text-muted-foreground mt-1">
                {t('trainingPage.heroDesc', 'Missioni pratiche reali per padroneggiare ogni funzione del CRM. Completa i moduli, colleziona i badge e ottieni il Certificato HCF.')}
              </p>
            </div>
            <div className="md:text-right shrink-0">
              <div className="text-2xl font-bold tabular-nums" data-testid="training-progress-percent">{progress.percent}%</div>
              <div className="text-xs text-muted-foreground tabular-nums">
                {progress.done}/{progress.total} {t('trainingPage.missionsLabel', 'missioni')} · {progress.points}/{progress.points_total} pt
              </div>
            </div>
          </div>
          <Progress value={progress.percent} className="h-2.5 mt-4" data-testid="training-progress-bar" />
          <div className="mt-4">
            {loading ? <Skeleton className="h-8 w-full" /> : <BadgesStrip badges={state.badges || []} />}
          </div>
        </CardContent>
      </Card>

      {/* Stato: obbligatorio / completato */}
      {locked && (
        <Card className="border-[#E4C88A] bg-[#FBF3DF]" data-testid="training-mandatory-alert">
          <CardContent className="p-4 flex items-start gap-3">
            <Lock className="w-5 h-5 text-[#B7791F] mt-0.5 shrink-0" />
            <div className="text-sm">
              <span className="font-semibold text-[#6A4B00]">{t('trainingPage.mandatoryTitle', 'Training obbligatorio.')}</span>{' '}
              <span className="text-[#6A4B00]/90">
                {t('trainingPage.mandatoryDesc', 'Completa tutte le missioni per certificarti: è richiesto una sola volta, poi il CRM sarà sempre libero. Naviga pure le sezioni del menu — le missioni si completano usando il CRM per davvero.')}
              </span>
              {nextMission && (
                <div className="mt-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      if (nextMission.type === 'quiz') setQuizMission(nextMission);
                      else if (nextMission.type === 'open') setOpenMission(nextMission);
                      else nav(nextMission.cta || '/app');
                    }}
                    data-testid="training-next-mission-btn"
                  >
                    {t('trainingPage.nextMission', 'Prossima missione:')} {nextMission.title} <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {completed && (
        <Card className="border-[#BFE3D3] bg-[#EAF6F0]" data-testid="training-completed-card">
          <CardContent className="p-5 flex flex-col md:flex-row md:items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#F3E7C7] border border-[#D6B25E] flex items-center justify-center shrink-0">
              <Trophy className="w-6 h-6 text-[#6A4B00]" />
            </div>
            <div className="flex-1">
              <div className="font-serif text-lg text-[#0C3C2D]">{t('trainingPage.certObtained', 'Certificato HCF ottenuto!')}</div>
              <p className="text-sm text-[#0C3C2D]/80">
                {t('trainingPage.completedDesc', { defaultValue: 'Hai completato tutte le {{total}} missioni ({{points}} punti). Puoi rifare il training quando vuoi come ripasso: non bloccherà mai più il CRM.', total: progress.total, points: progress.points_total })}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button onClick={() => downloadCertificate()} data-testid="training-certificate-btn">
                <FileDown className="w-4 h-4 mr-1" /> {t('trainingPage.downloadCert', 'Scarica attestato')}
              </Button>
              <Button variant="secondary" onClick={() => setRestartOpen(true)} data-testid="training-restart-btn">
                <RotateCcw className="w-4 h-4 mr-1" /> {t('trainingPage.redoTraining', 'Rifai il training')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Moduli */}
      {loading ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-40" />)}</div>
      ) : (
        (state.modules || []).map((mod, idx) => {
          const Icon = MODULE_ICONS[mod.icon] || Rocket;
          const modComplete = mod.done === mod.total;
          return (
            <Card key={mod.key} data-testid={`training-module-${mod.key}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    modComplete ? 'bg-[#D7EFE6] text-[#0C3C2D]' : 'bg-[var(--surface-3)] text-muted-foreground'
                  }`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                      <span className="text-muted-foreground font-normal">{t('trainingPage.moduleLabel', 'Modulo')} {idx + 1} ·</span> {mod.title}
                      {modComplete && (
                        <Badge className="bg-[#F3E7C7] text-[#6A4B00] border border-[#D6B25E] hover:bg-[#F3E7C7] gap-1">
                          <Award className="w-3 h-3" /> {mod.badge}
                        </Badge>
                      )}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">{mod.subtitle}</p>
                  </div>
                  <div className="text-xs font-semibold tabular-nums text-muted-foreground shrink-0">
                    {mod.done}/{mod.total}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 pt-0">
                {mod.missions.map((m) => (
                  <MissionRow
                    key={m.key}
                    mission={m}
                    onGo={(mission) => nav(mission.cta || '/app')}
                    onQuiz={(mission) => setQuizMission(mission)}
                    onOpen={(mission) => setOpenMission(mission)}
                  />
                ))}
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="training-page">
      {isAdmin ? (
        <Tabs defaultValue="mine">
          <TabsList data-testid="training-tabs">
            <TabsTrigger value="mine" data-testid="training-tab-mine">{t('trainingPage.myTraining', 'Il mio training')}</TabsTrigger>
            <TabsTrigger value="team" data-testid="training-tab-team">{t('trainingPage.teamProgress', 'Progressi del team')}</TabsTrigger>
          </TabsList>
          <TabsContent value="mine" className="mt-4">{myTraining}</TabsContent>
          <TabsContent value="team" className="mt-4"><TeamProgress /></TabsContent>
        </Tabs>
      ) : (
        myTraining
      )}

      <QuizDialog
        mission={quizMission}
        open={!!quizMission}
        onClose={() => setQuizMission(null)}
        onPassed={refresh}
      />

      {/* ★ Fase 59 — domande a risposta libera (obbligatorie per il certificato) */}
      <OpenAnswersDialog
        mission={openMission}
        open={!!openMission}
        onClose={() => setOpenMission(null)}
        onSaved={refresh}
      />

      <AlertDialog open={restartOpen} onOpenChange={setRestartOpen}>
        <AlertDialogContent data-testid="training-restart-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('trainingPage.restartDialogTitle', 'Rifare il training?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('trainingPage.restartDialogDesc', 'Le missioni ripartiranno da zero come ripasso. Il tuo Certificato HCF resta valido: il CRM non verrà bloccato.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="training-restart-cancel">{t('trainingPage.cancel', 'Annulla')}</AlertDialogCancel>
            <AlertDialogAction onClick={restart} data-testid="training-restart-confirm">{t('trainingPage.restartConfirm', 'Sì, ricomincia')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
